import React, { useState, useRef, useEffect, useMemo } from 'react';
import '../../../Stylesheets/Dashboard/Topbar.css';
import { FaBars, FaBell, FaUser, FaSignOutAlt, FaArrowRight } from 'react-icons/fa';
import {
  fetchNotifications,
  fetchUnreadCount,
  markNotificationRead,
  markAllNotificationsRead,
} from '../../../Redux/Notifications/notificationsSlice';
import { Link, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { logoutUser } from '../../../Redux/Auth/authActions';
import { fetchProjects } from '../../../Redux/Projects/projectSlice'; // <-- use existing slice


const Topbar = ({ onToggleSidebar }) => {



  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [imgError, setImgError] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [openNotif, setOpenNotif] = useState(false);
  const notifRef = useRef();
  const dropdownRef = useRef();
  const searchRef = useRef();
  const resultsRef = useRef();

  const dispatch = useDispatch();
  const navigate = useNavigate();


  // search state
  const [query, setQuery] = useState('');
  const [debounced, setDebounced] = useState('');
  const [openSearch, setOpenSearch] = useState(false);
  const [highlight, setHighlight] = useState(0);

  const { user, loading } = useSelector((s) => s.auth);
  const { list: projects, status: projectsStatus } = useSelector((s) => s.projects);
  const {
    items: notifs,
    unread,
    nextCursor,
    status: notifStatus,
    loadingMore,
  } = useSelector((s) => s.notifications || {});

  useEffect(() => {
    setImgError(false);
  }, [user?.photoURL]);



  // Debounce input
  useEffect(() => {
    const t = setTimeout(() => setDebounced(query.trim()), 200);
    return () => clearTimeout(t);
  }, [query]);

  // Load projects once when user focuses the search (or on first type)
  useEffect(() => {
    if (openSearch && projectsStatus === 'idle') {
      dispatch(fetchProjects());
    }
  }, [openSearch, projectsStatus, dispatch]);

  // Close dropdown when clicking outside
  // useEffect(() => {
  //   const handleClickOutside = (event) => {
  //     if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
  //       setDropdownOpen(false);
  //     }
  //   };
  //   document.addEventListener('mousedown', handleClickOutside);
  //   return () => document.removeEventListener('mousedown', handleClickOutside);
  // }, []);


  // One outside-click handler for dropdown, notifications, and search
  useEffect(() => {
    const handleOutside = (event) => {
      // Close user dropdown
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }

      // Close notifications if click is outside panel AND not on the bell
      const clickedBell = !!event.target.closest('.topbar-icon-bell-wrap');
      if (notifRef.current && !notifRef.current.contains(event.target) && !clickedBell) {
        setOpenNotif(false);
      }

      // Close search results if click is outside input/results
      if (
        searchRef.current &&
        resultsRef.current &&
        !searchRef.current.contains(event.target) &&
        !resultsRef.current.contains(event.target)
      ) {
        setOpenSearch(false);
      }
    };

    // use pointerdown for better mobile behavior
    document.addEventListener('pointerdown', handleOutside);
    return () => document.removeEventListener('pointerdown', handleOutside);
  }, []); // 👈 no stale state captured



  // Poll unread badge every 60s (and once on mount)
  useEffect(() => {
    dispatch(fetchUnreadCount());
    const id = setInterval(() => dispatch(fetchUnreadCount()), 60000);
    return () => clearInterval(id);
  }, [dispatch]);

  // Close dropdown on outside click
  // useEffect(() => {
  //   const handleClickOutside = (event) => {
  //     if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
  //       setDropdownOpen(false);
  //     }

  //     if (
  //       openNotif &&
  //       notifRef.current &&
  //       !notifRef.current.contains(event.target) &&
  //       !event.target.closest('.topbar-icon-bell-wrap')
  //     ) {
  //       setOpenNotif(false);
  //     }
  //     // close search results if click outside input/results
  //     if (
  //       openSearch &&
  //       searchRef.current &&
  //       resultsRef.current &&
  //       !searchRef.current.contains(event.target) &&
  //       !resultsRef.current.contains(event.target)
  //     ) {
  //       setOpenSearch(false);
  //     }
  //   };
  //   document.addEventListener('mousedown', handleClickOutside);
  //   return () => document.removeEventListener('mousedown', handleClickOutside);
  // }, [openSearch]);

  const handleLogout = () => {
    dispatch(logoutUser());
    navigate('/signin');
  };


  const toggleNotif = () => {
    const next = !openNotif;
    setOpenNotif(next);
    if (next) {
      // (re)load if empty
      if (!notifs || notifs.length === 0) {
        dispatch(fetchNotifications({ limit: 20 }));
      }
    }
  };

  const openNotification = (n) => {
    if (n._id && !n.readAt) dispatch(markNotificationRead(n._id));
    if (n.url) navigate(n.url);
    setOpenNotif(false);
  };

  const loadMoreNotifs = () => {
    if (nextCursor && !loadingMore) {
      dispatch(fetchNotifications({ cursor: nextCursor, limit: 20 }));
    }
  };



  // ---------- Presentation helpers ----------
  const displayName = useMemo(() => {
    const full = user?.name?.trim() || user?.email?.split('@')[0] || 'User';
    const parts = full.split(' ').filter(Boolean);
    if (parts.length >= 2) {
      return `${parts[0][0]}. ${parts.slice(-1)[0]}`;
    }
    return full;
  }, [user]);

  const initials = useMemo(() => {
    const base = user?.name?.trim() || user?.email || 'U';
    const parts = base.split(/[ ._@-]/).filter(Boolean);
    const first = parts[0]?.[0] || 'U';
    const last = parts.length > 1 ? parts[parts.length - 1][0] : '';
    return (first + last).toUpperCase();
  }, [user]);

  const seed = user?.uid || user?.email || 'site360-user';

  // Use a fallback that is reliable (dicebear sometimes fails depending on network/CSP).
  const placeholder = `https://ui-avatars.com/api/?name=${encodeURIComponent(
    user?.name || seed
  )}&background=EFEFEF&color=111&size=64&rounded=true`;

  // If image failed -> force placeholder
  const avatarUrl = imgError ? placeholder : (user?.photoURL || placeholder);

  // Optional: cache-bust ONLY in UI (don’t store this in DB)
  const avatarUrlDisplay = user?.photoURL
    ? `${avatarUrl}${avatarUrl.includes('?') ? '&' : '?'}v=${Date.now()}`
    : avatarUrl;




  // ----- Search filtering (client side) -----
  const results = useMemo(() => {
    if (!debounced) return [];
    const q = debounced.toLowerCase();
    // choose fields to search; adjust as needed
    return projects
      .filter((p) => {
        const name = (p.name || '').toLowerCase();
        const code = (p.code || '').toLowerCase();
        const addr = (p.address || '').toLowerCase();
        return name.includes(q) || code.includes(q) || addr.includes(q);
      })
      .slice(0, 8); // show top 8 matches
  }, [debounced, projects]);

  const goToProject = (id) => {
    setOpenSearch(false);
    setQuery('');
    setHighlight(0);
    navigate(`/dashboard/project/${id}`);
  };

  const onSearchKeyDown = (e) => {
    if (!openSearch) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlight((h) => Math.min(h + 1, results.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlight((h) => Math.max(h - 1, 0));
    } else if (e.key === 'Enter') {
      if (results[highlight]) {
        e.preventDefault();
        goToProject(results[highlight]._id);
      }
    } else if (e.key === 'Escape') {
      setOpenSearch(false);
    }
  };


  // small util for “3m ago”
  function formatTimeAgo(iso) {
    const now = Date.now();
    const t = new Date(iso).getTime();
    const s = Math.max(1, Math.floor((now - t) / 1000));
    if (s < 60) return `${s}s ago`;
    const m = Math.floor(s / 60);
    if (m < 60) return `${m}m ago`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h}h ago`;
    const d = Math.floor(h / 24);
    if (d < 7) return `${d}d ago`;
    return new Date(iso).toLocaleDateString();
  }


  return (
    <header className="dashboard-topbar">
      <button className="menu-toggle" onClick={onToggleSidebar}>
        <FaBars />
      </button>

      {/* Search */}
      <div className="topbar-search-wrap" ref={searchRef}>
        <input
          type="text"
          placeholder="Search projects…"
          className="topbar-search"
          value={query}
          onFocus={() => setOpenSearch(true)}
          onChange={(e) => { setQuery(e.target.value); setOpenSearch(true); }}
          onKeyDown={onSearchKeyDown}
          aria-autocomplete="list"
          aria-expanded={openSearch}
          aria-controls="topbar-search-results"
        />

        {openSearch && (debounced || projectsStatus === 'loading') && (
          <div
            id="topbar-search-results"
            className="topbar-search-results"
            ref={resultsRef}
            role="listbox"
          >
            {/* Loading state */}
            {projectsStatus === 'loading' && !projects.length && (
              <div className="topbar-search-item muted">Loading projects…</div>
            )}

            {/* No results */}
            {projectsStatus !== 'loading' && debounced && results.length === 0 && (
              <div className="topbar-search-item muted">No matches for “{debounced}”.</div>
            )}

            {/* Results */}
            {results.map((p, idx) => (
              <button
                key={p._id}
                className={`topbar-search-item ${idx === highlight ? 'active' : ''}`}
                onMouseEnter={() => setHighlight(idx)}
                onClick={() => goToProject(p._id)}
                role="option"
                aria-selected={idx === highlight}
              >
                <div className="topbar-search-item-main">
                  <div className="title">{p.name || 'Untitled Project'}</div>
                  {p.code || p.address ? (
                    <div className="sub">
                      {[p.code, p.address].filter(Boolean).join(' • ')}
                    </div>
                  ) : null}
                </div>
                <FaArrowRight className="chev" />
              </button>
            ))}

            {/* Optional “view all” */}
            {results.length > 0 && (
              <Link
                to={`/dashboard/projects?q=${encodeURIComponent(debounced)}`}
                className="topbar-search-viewall"
                onClick={() => setOpenSearch(false)}
              >
                View all results
              </Link>
            )}
          </div>
        )}
      </div>

      <div className="topbar-right">
        <div className="topbar-icon-bell-wrap" onClick={toggleNotif} role="button" aria-label="Notifications">
          <FaBell className="topbar-icon" />
          {unread > 0 && (
            <span className="notif-badge">{unread > 99 ? '99+' : unread}</span>
          )}
        </div>

        {openNotif && (
          <div className="notif-panel" ref={notifRef} role="dialog" aria-label="Notifications">
            <div className="notif-header">
              <div className="title">Notifications</div>
              {unread > 0 && (
                <button className="link-btn" onClick={() => dispatch(markAllNotificationsRead())}>
                  Mark all as read
                </button>
              )}
            </div>

            {notifStatus === 'loading' && !notifs?.length && (
              <div className="notif-empty">Loading…</div>
            )}

            {!notifs?.length && notifStatus === 'succeeded' && (
              <div className="notif-empty">No notifications yet.</div>
            )}

            <div className="notif-list">
              {notifs?.map((n) => (
                <button
                  key={n._id}
                  className={`notif-item ${n.readAt ? '' : 'unread'}`}
                  onClick={() => openNotification(n)}
                >
                  <div className="notif-item-left">
                    {n.icon ? (
                      <img className="notif-icon" src={n.icon} alt="" />
                    ) : (
                      <div className="notif-dot" />
                    )}
                  </div>
                  <div className="notif-item-main">
                    <div className="notif-title">{n.title}</div>
                    {n.body && <div className="notif-body">{n.body}</div>}
                    <div className="notif-meta">{formatTimeAgo(n.createdAt)}</div>
                  </div>
                </button>
              ))}
            </div>

            <div className="notif-footer">
              {nextCursor ? (
                <button className="btn-more" disabled={loadingMore} onClick={loadMoreNotifs}>
                  {loadingMore ? 'Loading…' : 'Show more'}
                </button>
              ) : (
                <div className="end-of-list">You’re up to date</div>
              )}
            </div>
          </div>
        )}

        <div
          className="topbar-dropdown-wrapper"
          ref={dropdownRef}
        >
          <div
            className={`topbar-avatar ${loading ? 'is-loading' : ''}`}
            onClick={() => !loading && setDropdownOpen((prev) => !prev)}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => (e.key === 'Enter' ? setDropdownOpen((p) => !p) : null)}
            aria-haspopup="menu"
            aria-expanded={dropdownOpen}
          >
            {avatarUrl && !imgError ? (
              <img
                key={user?.photoURL || 'no-photo'}   // force reload when URL changes
                src={avatarUrlDisplay}
                alt={user?.name || user?.email || 'User'}
                referrerPolicy="no-referrer"
                onError={() => setImgError(true)}
              />

            ) : (
              <div className="avatar-fallback" aria-hidden="true">{initials}</div>
            )}
            <span className="topbar-username">
              {loading ? 'Loading…' : displayName}
            </span>
          </div>

          {dropdownOpen && (
            <div className="dropdown-content show">
              <Link to="/dashboard/profile" className="dropdown-link">
                <FaUser /> View Profile
              </Link>
              <button onClick={() => setShowConfirm(true)}>
                <FaSignOutAlt /> Logout
              </button>
            </div>
          )}
        </div>
      </div>


      {showConfirm && (
        <div className="logout-modal-overlay">
          <div className="logout-modal">
            <h3>Confirm Logout</h3>
            <p>Are you sure you want to logout?</p>
            <div className="logout-modal-buttons">
              <button className="logout-modal-cancel-btn" onClick={() => setShowConfirm(false)}>Cancel</button>
              <button className="logout-modal-confirm-btn" onClick={handleLogout}>Yes, Logout</button>
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export default Topbar;
